import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import dayjs from "dayjs";
import { formatDate, formatPeriod } from "../../../hooks/helper";

const COLORS = [
  "#05304d",
  "#1F3F49",
  "#EA6A47",
  "#6AB187",
  "#488A99",
  "#DBAE58",
  "#0091D5",
  "#488A99",
];

type CaseBarChartProps = {
  chartsData?: {
    period: string;
    total_count: string;
    status_count: string;
    from_status?: string;
    to_status?: string;
    dayNumber?: number;
    weekNumber?: number;
    monthName?: number;
    job_count?: string;
    completed_count?: string;
    provisional_count?: string;
  }[];
  viewMode?: "day" | "week" | "month" | "year";
};
const lightenColor = (hex: string, amount = 0.4): string => {
  const num = parseInt(hex.slice(1), 16);
  const r = Math.min(255, ((num >> 16) & 0xff) + 255 * amount);
  const g = Math.min(255, ((num >> 8) & 0xff) + 255 * amount);
  const b = Math.min(255, (num & 0xff) + 255 * amount);
  return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
};

const CaseBarChart: React.FC<CaseBarChartProps> = ({
  chartsData = [],
  viewMode,
}) => {
  const processedData = chartsData.map((item, index) => {
    const value =
      item.total_count !== undefined
        ? Number(item.total_count)
        : item.status_count !== undefined
        ? Number(item.status_count)
        : item.job_count !== undefined
        ? Number(item.job_count)
        : 0;
    const statusCount = item.status_count ? Number(item.status_count) : 0;
    const baseColor = COLORS[index % COLORS.length];

    return {
      name:
        viewMode === "week"
          ? `Week ${item?.weekNumber}`
          : viewMode === "month"
          ? `${item?.monthName}`
          : dayjs(item.period).format("DD MMM"),
      value,
      statusCount,
      from_status: item.from_status || "N/A",
      to_status: item.to_status || "N/A",
      valueColor: baseColor,
      statusColor: lightenColor(baseColor),
      period: item.period,
    };
  });

  const CustomTooltip = ({ active, payload, label, period }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0]?.payload || {};
      const fromColor = data.valueColor || "#000";
      const toColor = data.statusColor || "#888";
       const rawPeriod = data.period || "N/A";
    const period = rawPeriod !== "N/A" ? formatPeriod(rawPeriod) : formatDate(rawPeriod);

      return (
        <div
          style={{
            backgroundColor: "#fff",
            padding: 8,
            border: "1px solid #ccc",
            fontSize: "12px",
          }}
        >
          <p>
            <strong>{period}</strong>
          </p>
          <p style={{ margin: 0 }}>
            <span style={{ color: fromColor, fontWeight: 500 }}>
              {data.from_status}:
            </span>{" "}
            {data.value}
          </p>
          <p style={{ margin: 0 }}>
            <span style={{ color: toColor, fontWeight: 500 }}>
              {data.to_status}:
            </span>{" "}
            {data.statusCount}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ width: "100%", height: 300 }}>
      <ResponsiveContainer>
        <BarChart data={processedData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="name"
            angle={-45}
            textAnchor="end"
            interval={0}
            height={36}
            fontSize={10}
          />
          <YAxis />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="value" stackId="a" barSize={20}>
            {processedData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[index % COLORS.length]}
              />
            ))}
          </Bar>
          <Bar dataKey="statusCount" stackId="a" barSize={20}>
            {processedData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={lightenColor(COLORS[index % COLORS.length])}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default CaseBarChart;
